<template>
  <div class="file-slide-media p-1">
    <video
      v-if="url != null"
      ref="videoEl"
      :src="url"
      :controls="controls"
      :autoplay="autoplay"
      loop
      :muted="muted"
      playsinline
      preload="metadata"
      :class="{
        'recently-change': recentlyChanged,
        'preview-image-shadow': !bigShadow,
        'preview-image-shadow-big': bigShadow,
      }"
      @ended="emit('ended')"
      @loadedmetadata="onLoadedMetadata"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useMinioSignedUrl } from "@/functions/useMinioSignedUrl";

const props = defineProps({
  fileUUID: {
    type: String,
    required: true,
  },
  autoplay: {
    type: Boolean,
    requried: false,
    default: false,
  },
  controls: {
    type: Boolean,
    required: false,
    default: true,
  },
  muted: {
    type: Boolean,
    required: false,
    default: true,
  },
  recentlyChanged: {
    type: Boolean,
    required: false,
    default: false,
  },
  bigShadow: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits<{
  ended: [];
  loadedmetadata: [durationInSeconds: number];
}>();

const videoEl = ref<HTMLVideoElement | null>(null);
const { url } = useMinioSignedUrl(computed(() => props.fileUUID));

async function tryPlay() {
  if (!props.autoplay || !videoEl.value) {
    return;
  }

  try {
    videoEl.value.currentTime = 0;
    await nextTick();
    await videoEl.value.play();
  } catch {
    // ignore autoplay failures
  }
}

function onLoadedMetadata() {
  const duration = videoEl.value?.duration;
  if (duration != null && Number.isFinite(duration) && duration > 0) {
    emit("loadedmetadata", duration);
  }
}

onMounted(() => {
  void tryPlay();
});

watch(
  () => url.value,
  () => {
    void tryPlay();
  },
);

watch(
  () => props.autoplay,
  () => {
    void tryPlay();
  },
);
</script>

<style lang="scss" scoped>
@use "@/modules/filemanagement/components/FMFileViewer/FMFileViewer.scss" as *;
.file-slide-media {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 0;
  overflow: visible;
}

video {
  display: block;
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}
</style>
